import { useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { buildLocationTelemetry } from "@/lib/location";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRACKING_STORAGE_VERSION = 1;
const getTrackingStorageKey = (userId, role = "driver") => role === "emergency" ? `eagle_emergency_tracking_${userId}` : `eagle_driver_tracking_${userId}`;
const getDriverStatusKey = (userId, role = "driver") => role === "emergency" ? `eagle_emergency_last_status_${userId}` : `eagle_driver_last_status_${userId}`;

const haversineMiles = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return earthRadiusMiles * c;
};

const DriverTrackingAgent = () => {
  const { user } = useAuth();
  const location = useLocation();
  const watchIdRef = useRef(null);
  const lastCoordRef = useRef(null);
  const milesRef = useRef(0);
  const driveSecondsRef = useRef(0);
  const timerRef = useRef(null);
  const lastUpdateSentAtRef = useRef(0);

  const shouldRun = ["driver", "emergency"].includes(user?.role) && !location.pathname.startsWith("/driver") && !location.pathname.startsWith("/emergency");

  useEffect(() => {
    if (!shouldRun || !navigator.geolocation || watchIdRef.current !== null) return;

    const userId = user?.id;
    if (!userId) return;

    const getCurrentStatus = () => localStorage.getItem(getDriverStatusKey(userId, user?.role)) || user?.driver_status || "off_duty";
    let permissionState = "unknown";

    const restoreSessionState = () => {
      try {
        const raw = localStorage.getItem(getTrackingStorageKey(userId, user?.role));
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed?.version !== TRACKING_STORAGE_VERSION) return;
        if (typeof parsed.milesDriven === "number") milesRef.current = parsed.milesDriven;
        if (typeof parsed.driveTimeSec === "number") driveSecondsRef.current = parsed.driveTimeSec;
        if (parsed.lastCoord) lastCoordRef.current = parsed.lastCoord;
      } catch (_) {
        // ignore broken local state
      }
    };

    const persistState = (liveSpeedMph = 0, coordsLike = null) => {
      const payload = {
        version: TRACKING_STORAGE_VERSION,
        currentStatus: getCurrentStatus(),
        isTracking: true,
        trackingPermission: permissionState,
        liveSpeedMph,
        driveTimeSec: driveSecondsRef.current,
        milesDriven: Number(milesRef.current.toFixed(2)),
        lastSeenAt: new Date().toISOString(),
        sessionStartedAt: null,
        lastCoord: lastCoordRef.current,
        lastKnownCoords: coordsLike
          ? {
              latitude: coordsLike.latitude,
              longitude: coordsLike.longitude,
              accuracy: coordsLike.accuracy,
              heading: coordsLike.heading,
              altitude: coordsLike.altitude,
            }
          : null,
      };
      localStorage.setItem(getTrackingStorageKey(userId, user?.role), JSON.stringify(payload));
    };

    const sendLocationUpdate = async (coords, speedMph) => {
      const now = Date.now();
      if (now - lastUpdateSentAtRef.current < 2000) return;
      lastUpdateSentAtRef.current = now;
      try {
        const locationPayload = await buildLocationTelemetry({
          latitude: coords.latitude,
          longitude: coords.longitude,
          lat: coords.latitude,
          lng: coords.longitude,
          accuracy_meters: coords.accuracy,
          speed_mph: speedMph,
          heading: coords.heading,
          altitude: coords.altitude,
          tracked_at: new Date().toISOString(),
          miles_driven_session: Number(milesRef.current.toFixed(2)),
          drive_time_seconds: driveSecondsRef.current,
        });
        await axios.put(`${API}/users/driver-status`, {
          status: getCurrentStatus(),
          location: locationPayload,
        });
      } catch (_) {
        // non-blocking
      }
    };

    restoreSessionState();

    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        permissionState = result.state;
      }).catch(() => {});
    }

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (getCurrentStatus() !== "on_duty") return;
      driveSecondsRef.current += 1;
      persistState();
    }, 1000);

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        permissionState = "granted";
        const coords = position.coords;
        const point = { latitude: coords.latitude, longitude: coords.longitude };

        let speedMph = 0;
        if (typeof coords.speed === "number" && !Number.isNaN(coords.speed) && coords.speed >= 0) {
          speedMph = coords.speed * 2.23694;
        } else if (lastCoordRef.current) {
          const segmentMiles = haversineMiles(lastCoordRef.current, point);
          const elapsedSec = (position.timestamp - (lastCoordRef.current.timestamp || position.timestamp)) / 1000;
          if (elapsedSec > 0) speedMph = (segmentMiles / elapsedSec) * 3600;
        }

        if (lastCoordRef.current) {
          const segmentMiles = haversineMiles(lastCoordRef.current, point);
          if (segmentMiles > 0.001) milesRef.current += segmentMiles;
        }

        lastCoordRef.current = { ...point, timestamp: position.timestamp };
        persistState(Number(speedMph.toFixed(1)), coords);
        await sendLocationUpdate(coords, Number(speedMph.toFixed(1)));
      },
      () => {
        permissionState = "denied";
        persistState();
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 },
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [shouldRun, user?.driver_status, user?.id, user?.role]);

  return null;
};

export default DriverTrackingAgent;
